// pkg134 — Terms + Privacy links under the sign-in card (pre-auth access; public pkg84 routes) — delivered 2026-07-27
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../App';
import { useFirmSettings } from '../contexts/FirmSettingsContext';

const REMEMBER_EMAIL_KEY = 'ryanlaw_remembered_email';

export default function LoginPage() {
  const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
  const [email,         setEmail]         = useState(savedEmail);
  const [password,      setPassword]      = useState('');
  const [rememberMe,    setRememberMe]    = useState(false);
  const [rememberEmail, setRememberEmail] = useState(!!savedEmail);
  const [error,         setError]         = useState('');
  const [loading,       setLoading]       = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { firm } = useFirmSettings();

  // Clear email from storage immediately when the checkbox is unchecked.
  useEffect(() => {
    if (!rememberEmail) {
      localStorage.removeItem(REMEMBER_EMAIL_KEY);
    }
  }, [rememberEmail]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Persist (or clear) email.
      if (rememberEmail && email.trim()) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, email.trim());
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }

      const data = await api.login(email.trim(), password, rememberMe);
      login(data.token, { ...data.client, must_change_password: data.mustChangePassword }, data.refreshToken);
      if (data.mustChangePassword) {
        navigate('/change-password');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-brand">
          <h1>{firm.firm_name || 'Firm Portal'}</h1>
          <p>Client Portal</p>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus={!savedEmail}
              required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus={!!savedEmail}
              required />
          </div>

          {/* Remember options */}
          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#555', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={rememberEmail}
                onChange={e => setRememberEmail(e.target.checked)}
                style={{ width: 15, height: 15, cursor: 'pointer' }}
              />
              Remember my email
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#555', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                style={{ width: 15, height: 15, cursor: 'pointer' }}
              />
              Stay logged in for 48 hours
            </label>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? <><span className="spinner" />Signing in...</> : 'Sign In'}
          </button>
        </form>
        {firm.phone && (
          <p style={{ marginTop: 24, fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
            Having trouble? Call {firm.phone}
          </p>
        )}
        {/* pkg134 — pre-auth legal links (parity with mobile pkg131: the
            policy must be reachable before sign-in too) */}
        <p style={{ marginTop: 12, fontSize: 11, color: '#aaa', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#999' }}>Terms of Service</a>
          <span style={{ margin: '0 6px' }}>·</span>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#999' }}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
